import React, { useCallback, useEffect, useRef, useState } from "react";
import "./VideoPopup.scss";
import Typography from "@mui/material/Typography";
import { Dialog, DialogContent, IconButton, Box, Fade, Slider } from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import ArrowBackIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardIos";
import { styled } from "@mui/material/styles";
import { useFetchVideoManifest } from "../../useQuery/useVideoManifest";
import { useAuthedMediaSrc } from "../../hooks";

const ASPECT_RATIO_DEFAULT = 16 / 9;
const PORTRAIT_THRESHOLD = 1;
const DIALOG_WIDTH_LANDSCAPE = 860;
const DIALOG_WIDTH_PORTRAIT = 380;

const PLAYBACK_SPEEDS = [0.5, 1, 1.25, 1.5, 2];

const BootstrapDialog = styled(Dialog)(() => ({
  "& .MuiDialogContent-root": {
    padding: 0,
  },
  "& .MuiPaper-root": {
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: "#0f0f10",
  },
}));

const formatTime = (secs) => {
  if (!secs || isNaN(secs)) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
};

const VideoPopup = ({ videoUrl, onClose, open, tcnameForvideo }) => {
  const {
    data: videos = [],
    isLoading: isManifestLoading,
    error: manifestError,
  } = useFetchVideoManifest(videoUrl, {
    enabled: open && !!videoUrl,
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [aspectRatio, setAspectRatio] = useState(ASPECT_RATIO_DEFAULT);
  const [videoReady, setVideoReady] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const videoRef = useRef(null);

  const {
    src: videoSrc,
    isLoading: isVideoLoading,
    error: videoError,
  } = useAuthedMediaSrc(open ? videos[currentIndex]?.url ?? null : null);

  const isLoading = isManifestLoading || (videos.length > 0 && isVideoLoading);
  const error = manifestError || videoError;

  // Reset local UI state when the dialog closes. Fetching is controlled
  // entirely by react-query's `enabled` flag above.
  useEffect(() => {
    if (!open) {
      setCurrentIndex(0);
      setAspectRatio(ASPECT_RATIO_DEFAULT);
      setVideoReady(false);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setIsMuted(false);
      setPlaybackRate(1);
    }
  }, [open]);

  // Reset playback state whenever the active video changes.
  useEffect(() => {
    setVideoReady(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setPlaybackRate(1);
  }, [currentIndex]);

  const handleLoadedMetadata = useCallback(() => {
    const el = videoRef.current;
    if (el && el.videoWidth && el.videoHeight) {
      setAspectRatio(el.videoWidth / el.videoHeight);
      setDuration(el.duration || 0);
    }
    if (el) el.playbackRate = playbackRate;
    setVideoReady(true);
  }, [playbackRate]);

  const handleTimeUpdate = useCallback(() => {
    if (!isSeeking && videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }, [isSeeking]);

  const handleVideoEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentIndex((prev) => (prev < videos.length - 1 ? prev + 1 : prev));
  }, [videos.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < videos.length - 1 ? prev + 1 : prev));
  }, [videos.length]);

  const togglePlay = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play();
      setIsPlaying(true);
    } else {
      el.pause();
      setIsPlaying(false);
    }
  }, []);

  const toggleMute = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setIsMuted(el.muted);
  }, []);

  const cycleSpeed = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    const idx = PLAYBACK_SPEEDS.indexOf(playbackRate);
    const next = PLAYBACK_SPEEDS[(idx + 1) % PLAYBACK_SPEEDS.length];
    el.playbackRate = next;
    setPlaybackRate(next);
  }, [playbackRate]);

  const toggleFullscreen = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (el.requestFullscreen) {
      el.requestFullscreen();
    }
  }, []);

  const handleSeekChange = useCallback((_, value) => {
    setIsSeeking(true);
    setCurrentTime(value);
  }, []);

  const handleSeekCommit = useCallback((_, value) => {
    const el = videoRef.current;
    if (el) {
      el.currentTime = value;
    }
    setIsSeeking(false);
  }, []);

  const isPortrait = aspectRatio < PORTRAIT_THRESHOLD;
  const maxDialogWidth = isPortrait ? DIALOG_WIDTH_PORTRAIT : DIALOG_WIDTH_LANDSCAPE;

  const hasVideos = !isLoading && !error && videos.length > 0 && !!videoSrc;
  const errorMessage = error ? error.message || "Failed to load video(s)." : "";

  return (
    <BootstrapDialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "auto",
          maxWidth: maxDialogWidth,
          maxHeight: "90vh",
          m: 2,
          transition: "max-width 0.25s ease",
        },
      }}
    >
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
          overflow: "hidden",
          width: maxDialogWidth,
        }}
      >
        <Box className="video-popup-header">
          <Box minWidth={0}>
            <Typography className="video-popup-title" noWrap>
              {tcnameForvideo || "Test Case Recording"}
            </Typography>
            {videos.length > 1 && (
              <Typography className="video-popup-subtitle">
                {videos[currentIndex]?.name || `Video ${currentIndex + 1}`}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={onClose} className="video-popup-close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box className="video-stage-wrapper">
          <Box
            className="video-stage"
            sx={{
              aspectRatio: `${aspectRatio}`,
              width: maxDialogWidth,
              maxWidth: maxDialogWidth,
              maxHeight: "calc(90vh - 140px)",
              overflow: "hidden",
            }}
          >
            {isLoading && (
              <Box className="video-state">
                <div className="spinner" />
                <Typography variant="body2">Loading recording…</Typography>
              </Box>
            )}

            {!isLoading && error && (
              <Box className="video-state">
                <Typography variant="body2" color="error.light">
                  {errorMessage}
                </Typography>
              </Box>
            )}

            {hasVideos && (
              <>
                <Fade in={videoReady} timeout={250}>
                  <video
                    ref={videoRef}
                    key={videoSrc}
                    autoPlay
                    onLoadedMetadata={handleLoadedMetadata}
                    onTimeUpdate={handleTimeUpdate}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={handleVideoEnded}
                    onClick={togglePlay}
                  >
                    <source src={videoSrc} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </Fade>
                {!videoReady && (
                  <Box className="video-state video-state--overlay">
                    <div className="spinner" />
                  </Box>
                )}
              </>
            )}
          </Box>

          {hasVideos && videos.length > 1 && currentIndex > 0 && (
            <button
              type="button"
              className="carousel-nav carousel-nav--prev"
              onClick={goToPrevious}
              aria-label="Previous video"
            >
              <ArrowBackIcon sx={{ fontSize: 14 }} />
            </button>
          )}

          {hasVideos && videos.length > 1 && currentIndex < videos.length - 1 && (
            <button
              type="button"
              className="carousel-nav carousel-nav--next"
              onClick={goToNext}
              aria-label="Next video"
            >
              <ArrowForwardIcon sx={{ fontSize: 14 }} />
            </button>
          )}
        </Box>

        {hasVideos && (
          <Box className="video-controls">
            <IconButton size="small" onClick={togglePlay} className="control-btn">
              {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>

            <Typography className="control-time">{formatTime(currentTime)}</Typography>

            <Slider
              size="small"
              value={currentTime}
              min={0}
              max={duration || 0}
              step={0.1}
              onChange={handleSeekChange}
              onChangeCommitted={handleSeekCommit}
              className="control-seek"
            />

            <Typography className="control-time">{formatTime(duration)}</Typography>

            <IconButton size="small" onClick={toggleMute} className="control-btn">
              {isMuted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
            </IconButton>

            <IconButton size="small" onClick={cycleSpeed} className="control-btn control-speed">
              <Typography component="span" className="control-speed-text">
                {playbackRate}x
              </Typography>
            </IconButton>

            <IconButton size="small" onClick={toggleFullscreen} className="control-btn">
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {videos.length > 1 && (
          <Box className="video-popup-dots">
            {videos.map((v, i) => (
              <Box
                key={v.url}
                className={`dot ${i === currentIndex ? "dot--active" : ""} ${
                  i < currentIndex ? "dot--played" : ""
                }`}
                onClick={() => setCurrentIndex(i)}
              />
            ))}
          </Box>
        )}
      </DialogContent>
    </BootstrapDialog>
  );
};

export default VideoPopup;
